class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartSuggestions extends HTMLElement {
  static requests = new Map();
  static selections = new Map();

  connectedCallback() {
    this.drawer = this.closest('cart-drawer');
    if (!this.drawer) return;
    if (this.drawer.classList.contains('active')) {
      this.load();
    } else {
      this.observer = new MutationObserver(() => {
        if (this.drawer.classList.contains('active')) {
          this.observer.disconnect();
          this.load();
        }
      });
      this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  features(item) {
    const text = (item.title + ' ' + item.description).toLowerCase();
    const title = item.title.toLowerCase();
    const design = text.split('finishing:')[0];
    const terms = ['blue', 'green', 'red', 'pink', 'purple', 'yellow', 'orange', 'white', 'black', 'mint', 'onyx', 'howlite', 'lapis', 'peridot', 'tourmaline', 'chrysoprase', 'opal', 'topaz', 'thulite', 'labradorite', 'garnet', 'coral', 'geometric', 'architectural', 'sculptural', 'flower', 'square', 'marquise'];
    return { terms: new Set(terms.filter(term => (design.match(/[a-z]+/g) || []).includes(term))), metal: ['gunmetal', 'gold', 'silver'].find(term => title.includes(term)), type: title.includes('ring') ? 'ring' : title.includes('cuff') ? 'cuff' : '' };
  }

  score(candidate, seeds) {
    const feature = this.features(candidate);
    return Math.max(...seeds.map(seed => {
      const source = this.features(seed);
      let score = 0;
      feature.terms.forEach(term => { if (source.terms.has(term)) score += 3; });
      if (feature.metal && feature.metal === source.metal) score += 1;
      if (feature.type && feature.type === source.type) score += 1;
      return score;
    }));
  }

  shuffle(items) {
    return items.map(item => ({ item, random: Math.random() })).sort((a, b) => a.random - b.random).map(entry => entry.item);
  }

  async candidates(seed) {
    if (!CartSuggestions.requests.has(seed.id)) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('section_id', 'cart-suggestions');
      url.searchParams.set('product_id', seed.id);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      CartSuggestions.requests.set(seed.id, fetch(url.toString()).then(response => {
        if (!response.ok) throw new Error('Recommendations unavailable');
        return response.text();
      }).then(html => {
        const document = new DOMParser().parseFromString(html, 'text/html');
        return [...document.querySelectorAll('.cart-suggestion')].map(card => ({
          id: card.dataset.productId,
          paired: card.dataset.paired === 'true',
          priority: Number(card.dataset.priority),
          data: JSON.parse(card.dataset.characteristics),
          card
        }));
      }).catch(() => []));
    }
    return CartSuggestions.requests.get(seed.id);
  }

  async load() {
    if (this.loading) return;
    this.loading = true;
    try {
      const items = JSON.parse(this.dataset.items);
      const seeds = [...new Map(items.map(item => [item.id, item])).values()];
      if (!seeds.length) return;
      const inCart = new Set(seeds.map(item => String(item.id)));
      const results = await Promise.all(seeds.map(seed => this.candidates(seed)));
      if (!this.isConnected) return;
      const unique = new Map();
      results.flat().forEach(candidate => {
        if (inCart.has(candidate.id)) return;
        const current = unique.get(candidate.id);
        if (!current || candidate.paired) unique.set(candidate.id, candidate);
      });
      const candidates = [...unique.values()];
      const paired = this.shuffle(candidates.filter(candidate => candidate.paired)).sort((a, b) => a.priority - b.priority);
      const hasRing = seeds.some(seed => seed.title.toLowerCase().includes('ring'));
      const related = candidates.filter(candidate => !candidate.paired && (!hasRing || candidate.data.title.toLowerCase().includes('ring'))).map(candidate => ({ ...candidate, score: this.score(candidate.data, seeds) })).sort((a, b) => b.score - a.score);
      const similar = related.filter(candidate => candidate.score > 0).slice(0, 6);
      const signature = [...inCart].sort().join(',');
      const previous = CartSuggestions.selections.get(signature) || [];
      const previousRelated = previous.map(id => similar.find(candidate => candidate.id === id)).filter(Boolean);
      const selection = [...paired, ...previousRelated, ...this.shuffle(similar).filter(candidate => !previousRelated.some(previous => previous.id === candidate.id))].slice(0, 3);
      CartSuggestions.selections.set(signature, selection.map(candidate => candidate.id));
      if (CartSuggestions.selections.size > 20) CartSuggestions.selections.delete(CartSuggestions.selections.keys().next().value);
      const target = this.querySelector('.cart-suggestions__items');
      target.replaceChildren(...selection.map(candidate => document.importNode(candidate.card, true)));
      this.querySelector('.cart-suggestions').hidden = selection.length === 0;
    } catch {
      this.querySelector('.cart-suggestions').hidden = true;
    }
  }
}

if (!customElements.get('cart-suggestions')) customElements.define('cart-suggestions', CartSuggestions);
